/** Freelancer marketplace: requirements open for applications, and my applications. Projects live in features/projects. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, inr, toPaise } from '../../core/format.js';
import { session } from '../../core/session.js';
import { reload, update } from '../../app/shell.js';
import { banner, bar, chips, empty, ic, pageHead, seg, statusTag, tag } from '../../ui/components.js';
import { field, inrField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

function applyState(r) {
  if (!r.application) return html`<button type="button" class="btn sm" data-act="apply.open" data-id="${r.id}" data-title="${r.title}">Apply</button>`;
  return statusTag(r.application);
}

const requirements = {
  path: '/professional/requirements',
  roles: ['professional'],
  title: 'Requirements',
  ui: () => ({ tab: 'open' }),
  async load(ctx) {
    const [open, mine] = await Promise.all([
      api.get('/freelancer/requirements', { signal: ctx.signal }),
      api.get('/freelancer/applications', { signal: ctx.signal }),
    ]);
    return { requirements: open.requirements, applications: mine.applications };
  },
  view(d, ui) {
    const verified = session.user.verification === 'verified';
    const live = d.applications.filter((a) => a.status === 'applied' || a.status === 'shortlisted').length;
    return html`${verified ? '' : banner('You can browse now. Applying opens once Vignova verifies your profile.', 'wn', 'shield')}
      ${pageHead('Requirements', 'Freelance and internship work from companies and startups, matched to your skills.', seg([['open', `Open (${d.requirements.length})`], ['mine', `My applications (${live})`]], ui.tab, 'work.tab', 'Requirements'))}
      ${ui.tab === 'open' ? openTab(d) : mineTab(d)}`;
  },
};

function openTab(d) {
  if (!d.requirements.length) return empty('clip', 'No open requirements', 'New requirements appear here as companies post them.');
  const sorted = [...d.requirements].sort((a, b) => b.fit_percent - a.fit_percent);
  return html`<div class="grid g2">${sorted.map((r) => html`<div class="card"><div class="row between">${tag(r.type)}<b class="sm">${r.fit_percent}% match</b></div>
    <h3 class="mt-s">${r.title}</h3><p class="sm mu">${r.org_name || 'A VConnect company'}${r.location ? `, ${r.location}` : ''}${r.work_mode ? `, ${r.work_mode}` : ''}${r.budget_text ? `, ${r.budget_text}` : ''}</p>
    ${chips(r.skills)}${bar(r.fit_percent, r.fit_percent >= 70 ? 'ok' : '', 'Skill match')}
    ${r.description ? html`<p class="sm mt-s" style="white-space:pre-line">${r.description.length > 240 ? `${r.description.slice(0, 240)}...` : r.description}</p>` : ''}
    <div class="row mt-s">${applyState(r)}${r.start_by ? html`<span class="xs mu">Needed from ${fmtDate(r.start_by)}</span>` : ''}</div></div>`)}</div>`;
}

function mineTab(d) {
  if (!d.applications.length) return empty('send', 'No applications yet', 'Apply to a requirement and track it here.');
  return html`<div class="list">${d.applications.map((a) => html`<div class="card mb"><div class="row between"><div><h3 style="margin:0">${a.requirement}</h3><p class="xs mu" style="margin:2px 0 0">Applied ${fmtDate(a.created_at)}. Your quote ${inr(a.quote_paise)}.</p></div>${statusTag(a.status)}</div>
    <p class="sm mt-s" style="white-space:pre-line">${a.proposal}</p>
    <div class="row">${a.status === 'shortlisted' && a.conversation_id ? html`<a class="btn sm" href="/professional/messages?c=${a.conversation_id}">${ic('chat')} Open chat</a>` : ''}
      ${a.status === 'applied' || a.status === 'shortlisted' ? html`<button type="button" class="btn ghost sm" data-act="apply.withdraw" data-id="${a.id}" data-title="${a.requirement}">Withdraw</button>` : ''}</div></div>`)}</div>`;
}

register({
  'work.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'apply.open': (el) => openModal({
    title: `Apply: ${el.dataset.title}`,
    body: html`<form id="apply-form" data-submit="apply.send" data-id="${el.dataset.id}" novalidate>
      ${field('ap-proposal', 'Your proposal', { name: 'proposal', rows: 6, minlen: 20, max: 3000, ph: 'What you have built that is similar, how you would approach this, and when you can start.', err: 'Write at least 20 characters.' })}
      ${inrField('ap-quote', 'Your quote for the whole job', { name: 'quote', number: true, ph: '25000', help: 'In rupees. You and the client agree the final amount.' })}</form>
      <p class="note">The company sees your proposal and your VConnect profile. Chat opens if they shortlist you.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="apply-form" class="btn">Send application</button>`,
  }),
  'apply.send': (form) => submitForm(form, async ({ proposal, quote }) => {
    const paise = toPaise(quote);
    if (!Number.isFinite(paise) || paise < 0) throw new Error('Enter your quote in rupees.');
    await api.post(`/freelancer/requirements/${form.dataset.id}/apply`, { body: { proposal, quote_paise: paise } });
    closeModal(false);
    toast('Application sent', 'send');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'apply.withdraw': async (el) => {
    if (!(await confirmBox({ title: `Withdraw from ${el.dataset.title}?`, text: 'The company will no longer see your application.', label: 'Withdraw', danger: true }))) return;
    await api.post(`/freelancer/applications/${el.dataset.id}/withdraw`);
    toast('Application withdrawn', 'out');
    await reload();
  },
});

export const routes = [requirements];
